import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { logout as apiLogout } from "../api";

interface User {
  id: number;
  email: string;
  name: string;
  picture: string;
  signature_url: string | null;
  role: string;
  is_super_admin: boolean;
  timezone: string;
  requires_2fa_enrollment: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  refreshAuth: () => Promise<User | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null, loading: true,
  refreshAuth: async () => null, logout: () => {}
});

export function AuthProvider({ children }: React.PropsWithChildren<{}>) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback((): Promise<User | null> => {
    return api.get("/api/auth/me")
      .then(r => { setUser(r.data); return r.data as User; })
      .catch(() => { setUser(null); return null; })
      .finally(() => setLoading(false));
  }, []);

  const logout = async () => {
    await apiLogout().catch(() => {});
    setUser(null);
  };

  useEffect(() => {
    // Public-only routes: don't block render waiting for auth.
    // Set loading=false immediately so the page shows instantly.
    // refreshAuth() still runs in background — if the user IS logged in,
    // the app will re-render and redirect them automatically.
    const path = window.location.pathname;
    const isPublic = path === "/" || /^\/(pricing|docs|login|signup|invite|setup|careers|interview-confirm|interview-availability|offer-approval\/|offer\/)/.test(path);
    if (isPublic) setLoading(false);
    refreshAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refreshAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
